// Shared laboratory-test catalog + lookup. Used by:
//   - PatientDetail.jsx (Investigations table with auto-fill of unit/ref-range)
//   - components/ManualLabEntry.jsx (Reception's manual lab-entry form)
//
// Adding a new test: append to the appropriate group. `name` must be
// unique across the whole catalog (LAB_LOOKUP dedupes by exact match).

export const LAB_CATALOG = [
  { group: "Glycaemic", tests: [
    { name: "FBS", unit: "mg/dL", ref: "70-100" },
    { name: "PPBS", unit: "mg/dL", ref: "< 140" },
    { name: "Random Sugar", unit: "mg/dL", ref: "< 200" },
    { name: "HbA1c", unit: "%", ref: "< 5.7" },
    { name: "Fasting Insulin", unit: "µIU/mL", ref: "2-25" },
    { name: "C-Peptide", unit: "ng/mL", ref: "0.5-2.0" },
    { name: "Fructosamine", unit: "µmol/L", ref: "205-285" },
  ]},
  { group: "Renal Function Profile", tests: [
    { name: "Urea", unit: "mg/dL", ref: "15-40" },
    { name: "Blood Urea Nitrogen", unit: "mg/dL", ref: "7-20" },
    { name: "Creatinine", unit: "mg/dL", ref: "0.6-1.2" },
    { name: "Uric Acid", unit: "mg/dL", ref: "3.5-7.2" },
    { name: "Sodium", unit: "mEq/L", ref: "135-145" },
    { name: "Potassium", unit: "mEq/L", ref: "3.5-5.1" },
    { name: "Chloride", unit: "mEq/L", ref: "98-107" },
    { name: "Bicarbonate", unit: "mEq/L", ref: "22-29" },
    { name: "Calcium", unit: "mg/dL", ref: "8.5-10.5" },
    { name: "Phosphorus", unit: "mg/dL", ref: "2.5-4.5" },
  ]},
  { group: "Liver Function Profile", tests: [
    { name: "Bilirubin Total", unit: "mg/dL", ref: "0.2-1.2" },
    { name: "Bilirubin Direct", unit: "mg/dL", ref: "0.0-0.3" },
    { name: "Bilirubin Indirect", unit: "mg/dL", ref: "0.1-0.9" },
    { name: "AST", unit: "U/L", ref: "5-40" },
    { name: "ALT", unit: "U/L", ref: "7-56" },
    { name: "ALP", unit: "U/L", ref: "44-147" },
    { name: "GGT", unit: "U/L", ref: "9-48" },
    { name: "Total Protein", unit: "g/dL", ref: "6.4-8.3" },
    { name: "Albumin", unit: "g/dL", ref: "3.5-5.0" },
    { name: "Globulin", unit: "g/dL", ref: "2.0-3.5" },
    { name: "A/G Ratio", unit: "", ref: "1.1-2.5" },
  ]},
  { group: "Lipid Profile", tests: [
    { name: "Total Cholesterol", unit: "mg/dL", ref: "< 200" },
    { name: "HDL", unit: "mg/dL", ref: "> 40" },
    { name: "LDL", unit: "mg/dL", ref: "< 100" },
    { name: "Triglycerides", unit: "mg/dL", ref: "< 150" },
    { name: "VLDL", unit: "mg/dL", ref: "< 30" },
    { name: "Non-HDL", unit: "mg/dL", ref: "< 130" },
  ]},
  { group: "Urine", tests: [
    { name: "Urine Albumin", unit: "mg/L", ref: "< 20" },
    { name: "Urine Creatinine", unit: "mg/dL", ref: "20-320" },
    { name: "UACR (Urine ACR)", unit: "mg/g", ref: "< 30" },
    { name: "Urine Routine — Colour", unit: "", ref: "Pale yellow" },
    { name: "Urine Routine — Specific Gravity", unit: "", ref: "1.005-1.030" },
    { name: "Urine Routine — pH", unit: "", ref: "4.5-8.0" },
    { name: "Urine Routine — Protein", unit: "", ref: "Nil / Trace" },
    { name: "Urine Routine — Glucose", unit: "", ref: "Nil" },
    { name: "Urine Routine — Ketones", unit: "", ref: "Nil" },
    { name: "Urine Routine — RBC", unit: "/hpf", ref: "0-2" },
    { name: "Urine Routine — Pus Cells", unit: "/hpf", ref: "0-5" },
    { name: "Urine Routine — Epithelial Cells", unit: "/hpf", ref: "Few" },
  ]},
  { group: "Inflammation", tests: [
    { name: "CRP", unit: "mg/L", ref: "< 10" },
    { name: "hs-CRP", unit: "mg/L", ref: "< 1 low, 1-3 avg, > 3 high" },
    { name: "ESR", unit: "mm/hr", ref: "M 0-15 / F 0-20" },
    { name: "Procalcitonin", unit: "ng/mL", ref: "< 0.5" },
  ]},
  { group: "Arthritis Profile", tests: [
    { name: "RA Factor", unit: "IU/mL", ref: "< 14" },
    { name: "Anti-CCP", unit: "U/mL", ref: "< 20" },
    { name: "ANA (IF)", unit: "", ref: "Negative" },
    { name: "Anti-dsDNA", unit: "IU/mL", ref: "< 30" },
    { name: "Complement C3", unit: "mg/dL", ref: "83-193" },
    { name: "Complement C4", unit: "mg/dL", ref: "15-57" },
    { name: "HLA-B27", unit: "", ref: "Negative" },
    // Uric Acid deliberately omitted here — it lives in Renal Function Profile
    // above so the picker/datalist never renders duplicate keys.
  ]},
  { group: "Hemogram (CBC)", tests: [
    { name: "Haemoglobin", unit: "g/dL", ref: "M 13-17 / F 12-15" },
    { name: "Total WBC", unit: "×10³/µL", ref: "4-11" },
    { name: "RBC Count", unit: "×10⁶/µL", ref: "M 4.5-5.5 / F 4.0-5.0" },
    { name: "Platelets", unit: "×10³/µL", ref: "150-450" },
    { name: "PCV / HCT", unit: "%", ref: "M 40-50 / F 36-46" },
    { name: "MCV", unit: "fL", ref: "80-100" },
    { name: "MCH", unit: "pg", ref: "27-32" },
    { name: "MCHC", unit: "g/dL", ref: "32-36" },
    { name: "RDW", unit: "%", ref: "11.5-14.5" },
    { name: "Neutrophils", unit: "%", ref: "40-70" },
    { name: "Lymphocytes", unit: "%", ref: "20-45" },
    { name: "Monocytes", unit: "%", ref: "2-10" },
    { name: "Eosinophils", unit: "%", ref: "1-6" },
    { name: "Basophils", unit: "%", ref: "0-2" },
  ]},
  { group: "Cardiac Profile", tests: [
    { name: "Troponin I", unit: "ng/mL", ref: "< 0.04" },
    { name: "Troponin T (hs)", unit: "ng/L", ref: "< 14" },
    { name: "CK-MB", unit: "U/L", ref: "< 25" },
    { name: "Total CPK", unit: "U/L", ref: "M 55-170 / F 30-135" },
    { name: "LDH", unit: "U/L", ref: "140-280" },
    { name: "NT-proBNP", unit: "pg/mL", ref: "< 125 (< 75y) / < 450 (≥75y)" },
    { name: "BNP", unit: "pg/mL", ref: "< 100" },
    { name: "D-Dimer", unit: "µg/mL FEU", ref: "< 0.5" },
    { name: "Homocysteine", unit: "µmol/L", ref: "< 15" },
    { name: "2D Echo — Ejection Fraction", unit: "%", ref: "55-70" },
    { name: "2D Echo — LVIDd", unit: "mm", ref: "M 42-59 / F 39-53" },
    { name: "2D Echo — Wall Motion", unit: "", ref: "Normal" },
  ]},
  { group: "Iron Studies", tests: [
    { name: "Serum Iron", unit: "µg/dL", ref: "M 65-176 / F 50-170" },
    { name: "TIBC", unit: "µg/dL", ref: "240-450" },
    { name: "UIBC", unit: "µg/dL", ref: "112-347" },
    { name: "Transferrin Saturation", unit: "%", ref: "20-50" },
    { name: "Ferritin", unit: "ng/mL", ref: "M 30-400 / F 15-150" },
  ]},
  { group: "Anaemia Workup", tests: [
    { name: "Reticulocyte Count", unit: "%", ref: "0.5-2.5" },
    { name: "Peripheral Smear", unit: "", ref: "Normocytic normochromic" },
  ]},
  { group: "Thyroid", tests: [
    { name: "TSH", unit: "µIU/mL", ref: "0.4-4.0" },
    { name: "Free T3", unit: "pg/mL", ref: "2.3-4.2" },
    { name: "Free T4", unit: "ng/dL", ref: "0.8-1.8" },
    { name: "Anti-TPO", unit: "IU/mL", ref: "< 35" },
  ]},
  { group: "Vitamins / Endocrine", tests: [
    { name: "Vitamin D (25-OH)", unit: "ng/mL", ref: "30-100" },
    { name: "Vitamin B12", unit: "pg/mL", ref: "200-900" },
    { name: "Folate", unit: "ng/mL", ref: "> 4" },
    { name: "PTH (Intact)", unit: "pg/mL", ref: "15-65" },
    { name: "Cortisol (AM)", unit: "µg/dL", ref: "6.2-19.4" },
    { name: "Prolactin", unit: "ng/mL", ref: "M 4-15 / F 4-23" },
  ]},
];

// Flat lookup by canonical test name → { unit, ref } for the auto-fill logic.
export const LAB_LOOKUP = Object.fromEntries(
  LAB_CATALOG.flatMap((g) => g.tests.map((t) => [t.name, { unit: t.unit, ref: t.ref }]))
);

// Predefined multi-test packages so one click adds an entire profile at once.
// Used by both the doctor's Investigations picker and the reception's
// Manual Lab Entry form so both roles pull the same profiles.
export const LAB_PACKAGES = [
  { key: "RFT", label: "Renal Function Profile", tests: LAB_CATALOG.find((g) => g.group === "Renal Function Profile").tests.map((t) => t.name) },
  { key: "LFT", label: "Liver Function Profile", tests: LAB_CATALOG.find((g) => g.group === "Liver Function Profile").tests.map((t) => t.name) },
  { key: "Lipid", label: "Lipid Profile", tests: LAB_CATALOG.find((g) => g.group === "Lipid Profile").tests.map((t) => t.name) },
  { key: "CBC", label: "Hemogram (CBC)", tests: LAB_CATALOG.find((g) => g.group === "Hemogram (CBC)").tests.map((t) => t.name) },
  { key: "URINE", label: "Urine Routine", tests: LAB_CATALOG.find((g) => g.group === "Urine").tests.filter((t) => t.name.startsWith("Urine Routine")).map((t) => t.name) },
  { key: "ARTHR", label: "Arthritis Profile", tests: LAB_CATALOG.find((g) => g.group === "Arthritis Profile").tests.map((t) => t.name) },
  { key: "CARDIAC", label: "Cardiac Profile", tests: LAB_CATALOG.find((g) => g.group === "Cardiac Profile").tests.map((t) => t.name) },
  { key: "IRON", label: "Iron Study Profile", tests: LAB_CATALOG.find((g) => g.group === "Iron Studies").tests.map((t) => t.name) },
  { key: "THYROID", label: "Thyroid Profile", tests: LAB_CATALOG.find((g) => g.group === "Thyroid").tests.map((t) => t.name) },
  { key: "DIAB", label: "Diabetes Screening (FBS + PPBS + HbA1c + UACR)", tests: ["FBS", "PPBS", "HbA1c", "UACR (Urine ACR)"] },
  // BSH-IDA 2021 + BSH B12/folate aligned anaemia workup — CBC, retic,
  // smear, full iron studies and B12/folate in one tap.
  { key: "ANAEMIA", label: "Anaemia Workup (BSH) — CBC + Retic + Iron + B12/Folate", tests: [
    ...LAB_CATALOG.find((g) => g.group === "Hemogram (CBC)").tests.map((t) => t.name),
    "Reticulocyte Count", "Peripheral Smear",
    "Ferritin", "Serum Iron", "TIBC", "Transferrin Saturation",
    "Vitamin B12", "Folate",
  ] },
];
